// src/lib/timezone.js
// Utility for getting user's preferred timezone with localStorage override

const TIMEZONE_STORAGE_KEY = "userTimezone";

/**
 * Get the browser's timezone using Intl API
 * @returns {string} The browser's timezone (e.g., "Africa/Nairobi")
 */
export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
};

/**
 * Get the user's preferred timezone.
 * Priority: localStorage (user preference) > browser timezone
 * @returns {string} The user's timezone
 */
export const getUserTimezone = () => {
  try {
    const storedTimezone = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (storedTimezone && storedTimezone.trim()) {
      return storedTimezone.trim();
    }
  } catch {
    // localStorage might not be available
  }
  return getBrowserTimezone();
};

/**
 * Save the user's preferred timezone to localStorage
 * @param {string} timezone - The timezone to save
 */
export const setUserTimezone = (timezone) => {
  try {
    if (timezone && timezone.trim()) {
      localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone.trim());
    }
  } catch {
    // localStorage might not be available
    console.warn("Could not save timezone to localStorage");
  }
};

/**
 * Clear the stored timezone from localStorage
 */
export const clearUserTimezone = () => {
  try {
    localStorage.removeItem(TIMEZONE_STORAGE_KEY);
  } catch {
    // localStorage might not be available
  }
};

export { TIMEZONE_STORAGE_KEY };

/**
 * Parse a UTC date/time value from various formats and return a Date object.
 * - Numbers are treated as seconds when < 1e12, otherwise milliseconds.
 * - Strings without explicit timezone info are assumed to be UTC and 'Z' is appended.
 */
const parseUtcDate = (value) => {
  if (value == null) return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") {
    // Treat as seconds when reasonably small
    const ms = value < 1e12 ? value * 1000 : value;
    return new Date(ms);
  }
  if (typeof value === "string") {
    const s = value.trim();
    // If string already contains timezone info (Z or ±HH or ±HH:MM), use it as-is
    if (/[zZ]|[+\-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
    // Otherwise assume it's UTC and append 'Z'
    return new Date(s.endsWith("Z") ? s : s + "Z");
  }
  return null;
};

/**
 * Format a UTC datetime value into the user's timezone for display.
 * Returns a human-friendly string like "Mar 19, 2026, 10:15 AM (GMT+3)".
 */
export const formatUtcToUserTimezone = (value, timezone = null, options = {}) => {
  try {
    const tz = timezone || getUserTimezone();
    const d = parseUtcDate(value);
    if (!d) return "Not available";

    const baseOpts = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      ...options,
      timeZone: tz,
    };

    const dateStr = new Intl.DateTimeFormat(undefined, baseOpts).format(d);

    try {
      const parts = new Intl.DateTimeFormat(undefined, {
        timeZone: tz,
        timeZoneName: "shortOffset",
      }).formatToParts(d);
      const tzName = parts.find((p) => p.type === "timeZoneName")?.value || tz;
      return `${dateStr} (${tzName})`;
    } catch {
      return `${dateStr} (${tz})`;
    }
  } catch (e) {
    return String(value);
  }
};

